var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _FsCurrencyField_instances, _FsCurrencyField_onKeydown, _FsCurrencyField_onInvalid, _FsCurrencyField_setInstanceAndFormValue;
import 'element-internals-polyfill';
import { canonicalName, CurrencyFieldEventType, CurrencyFieldManager, currencyFieldManagerConfigDefaults } from '@cof/fs-currency-field-core-beta';
import labelStyles from '@cof/fs-field-label-core/lib/field-label.lit.min.js';
import formFieldStyles from '@cof/fs-form-field-core/lib/form-field.lit.min.js';
import helperTextStyles from '@cof/fs-helper-text-core/lib/helper-text.lit.min.js';
import textFieldStyles from '@cof/fs-text-field-core/lib/text-field.lit.min.js';
import { FormControlMixin, requiredValidator } from '@open-wc/form-control';
import { submit } from '@open-wc/form-helpers';
import { css, html, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { maxValidatorCurrency, minValidatorCurrency, setCustomValidity } from './currency-validators.js';
/**
 * The web component implementation of the Financial Services currency field component
 *
 * ## Installation
 *
 * ```bash
 * npm i @cof/fs-currency-field-wc-beta
 * ```
 *
 * ## Usage
 *
 * To import the side-effectful registration of `<fs-currency-field>` call
 *
 * ```javascript
 * import '@cof/fs-currency-field-wc-beta/fs-currency-field.js';
 * ```
 *
 * To import the `FsCurrencyField` class as either a base class or to self-register the custom element use
 *
 * ```javascript
 * import { FsCurrencyField } from '@cof/fs-currency-field-wc-beta';
 * ```
 *
 * @example
 * ```html
 * <fs-currency-field
 *   currency-sign="$"
 *   currency-sign-position="start"
 *   decimal-separator="."
 *   max="200000"
 *   maximum-fraction-digits="2"
 *   min="0"
 *   minimum-decimal-percision="2"
 *   minimum-fraction-digits="0"
 *   name="down-payment"
 *   thousands-separator=","
 * >
 *   Down payment
 * </fs-disclosure>
 *
 * ```
 *
 * @element fs-currency-field
 * @fires input - Fired when the control's value changes on keyboard interaction
 * @fires change - Notifies when the control's value is committed (typically on blur)
 * @slot leading-icon - The text field's leading icon which is typically non-interactive per the rules in the component's design spec
 * @slot trailing-icon - The text field's trailing icon which is typically an interactive icon per the component's design spec
 * @slot - The text field's label text
 */
export class FsCurrencyField extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        _FsCurrencyField_instances.add(this);
        /** The autocomplete settings for the input */
        this.autocomplete = 'off';
        /** If true, the browser should automatically focus on the input */
        this.autofocus = false;
        /** If true, the input will use compact styles */
        this.compact = false;
        /**
         * The sign to apply to the currency field
         * @attribute currency-sign
         */
        this.currencySign = currencyFieldManagerConfigDefaults.currencySign;
        /**
         * The location of the currency sign relative to the currency value
         * possible values are "start" and "end"
         * @attribute currency-sign-position
         */
        this.currencySignPosition = currencyFieldManagerConfigDefaults.currencySignPosition;
        /**
         * The character used to separate the decimal spot in the currency value
         * @attribute decimal-separator
         */
        this.decimalSeparator = currencyFieldManagerConfigDefaults.decimalSeparator;
        /** If true, the input will be disabled */
        this.disabled = false;
        /**
         * If set, helper text will be displayed below the input
         * @attribute helper-text
         */
        this.helperText = '';
        /**
         * If true, the label will be visually hidden but remain for a11y purposes
         * @attribute hide-label
         */
        this.hideLabel = false;
        /** The maximum value a currency field can be in and remain in a valid state */
        this.max = currencyFieldManagerConfigDefaults.max;
        /**
         * The minimum number of decimals required to be included in the currency value
         * @attribute minimum-decimal-percision
         */
        this.minimumDecimalPrecision = currencyFieldManagerConfigDefaults.minimumDecimalPrecision;
        /**
         * The maximum number of allowable digits after the decimal separator
         * attribute maximum-fraction-digits
         */
        this.maximumFractionDigits = currencyFieldManagerConfigDefaults.maximumFractionDigits;
        /** The minimum value a currency field can be in and remain in a valid state */
        this.min = currencyFieldManagerConfigDefaults.min;
        /** A custom error message */
        this.errorMessage = '';
        /**
         * The minimum number of allowable digits after the decimal separator
         * attribute minimum-fraction-digits
         */
        this.minimumFractionDigits = currencyFieldManagerConfigDefaults.minimumFractionDigits;
        /**
         * If true, the input will not use input validators
         * @attribute no-validate
         */
        this.noValidate = false;
        /** The placeholder string on the input element. Should not be relied upon as a label */
        this.placeholder = '';
        /**
         * Works in conjunction with the `max` property. If the field value exceeds the value
         * set for max, use the error passed in to this property to override the default
         * validation message.
         * @attribute range-overflow-message
         */
        this.rangeOverflowMessage = '';
        /**
         * Works in conjunction with the `min` property. If the field value does not meet
         * or exceed the value set for min, use the error passed in to this property
         * to override the default validation message.
         * @attribute range-underflow-message
         */
        this.rangeUnderflowMessage = '';
        /**
         * A proxy for the input's readonly attribute
         * @attribute readonly
         */
        this.readOnly = false;
        /** If true, the input is required if used in the context of a form */
        this.required = false;
        /**
         * The character used for the thousands separator
         * @attribute thousands-separator
         */
        this.thousandsSeparator = currencyFieldManagerConfigDefaults.thousandsSeparator;
        /**
         * When set to true the social security number field will use a formatted
         * value when participating in form submission
         */
        this.useMaskedValue = currencyFieldManagerConfigDefaults.useMaskedValue;
        /** The error message for the text field */
        this.validationMessage = '';
        /** Allows the developer to override the default value missing error message */
        this.valueMissingMessage = '';
        /** Should the error be shown */
        this._showError = false;
        /**
         * Whenever the control identifies as invalid, we need to cancel
         * the default behavior of popping up the native validation display
         */
        _FsCurrencyField_onInvalid.set(this, (event) => {
            event.preventDefault();
        });
    }
    get managerConfig() {
        return {
            currencySign: this.currencySign,
            currencySignPosition: this.currencySignPosition,
            decimalSeparator: this.decimalSeparator,
            max: this.max,
            maximumFractionDigits: this.maximumFractionDigits,
            min: this.min,
            minimumDecimalPrecision: this.minimumDecimalPrecision,
            minimumFractionDigits: this.minimumFractionDigits,
            thousandsSeparator: this.thousandsSeparator,
            useMaskedValue: this.useMaskedValue
        };
    }
    get maskedValue() {
        return this.manager?.maskedValue;
    }
    get preferredValue() {
        return this.useMaskedValue ? this.manager.maskedValue : this.manager.unmaskedValue;
    }
    get unmaskedValue() {
        return this.manager?.unmaskedValue;
    }
    /**
     * Invoked when the component is added to the document's DOM.
     * This connects (or reconnects) the CurrencyFieldManager to the input and adds needed event listeners.
     */
    connectedCallback() {
        super.connectedCallback();
        if (this.manager != null && !this.manager.isConnected) {
            this.manager.connect();
        }
        this.addEventListener('invalid', __classPrivateFieldGet(this, _FsCurrencyField_onInvalid, "f"));
    }
    /**
     * Invoked when the component is removed from the document's DOM.
     * This disconnects the CurrencyFieldManager from the input and removes event listeners.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.manager?.isConnected) {
            this.manager.disconnect();
        }
        this.removeEventListener('invalid', __classPrivateFieldGet(this, _FsCurrencyField_onInvalid, "f"));
    }
    /**
     * When the field is first updated, create a new instances of the manager
     * and connect it to the DOM. We also want to check for any initial value and
     * process that using the manager and set the form value as the provided value.
     */
    firstUpdated() {
        this.manager = new CurrencyFieldManager(this.renderRoot.querySelector('.fs-form-field'));
        this.manager.connect();
        if (this.value) {
            this.manager.setValue(this.value);
            this.setValue(this.value);
        }
        else {
            this.setValue('');
        }
        this.tabIndex = 0;
        this.manager.addEventListener(CurrencyFieldEventType['currency-field-change'], __classPrivateFieldGet(this, _FsCurrencyField_instances, "m", _FsCurrencyField_setInstanceAndFormValue));
    }
    render() {
        return html `
      <div class="fs-form-field ${this.compact ? 'fs-form-field--compact' : ''}">
        <label
          class="fs-field-label
          ${this.compact ? 'fs-field-label--compact' : ''}
          ${this.disabled ? 'fs-field-label--disabled' : ''}
          ${this.hideLabel ? 'fs-field-label--hidden' : ''}"
          for="input">
          <slot></slot> 
          ${this.required ? '' : html `<span class="fs-field-label__optional">(optional)</span>`}
        </label>
        <div
          class="fs-text-field
          ${this.compact ? 'fs-text-field--compact' : ''}
          ${this.disabled ? 'fs-text-field--disabled' : ''}
          ${this._showError && !this.disabled ? 'fs-text-field--error' : ''}
          ${this.readOnly ? 'fs-text-field--readonly' : ''}">
          <slot name="leading-icon"></slot>
          <input
            id="input"
            autocomplete="${this.autocomplete}"
            aria-describedby="helper-text"
            ?autofocus="${this.autofocus}"
            ?disabled="${this.disabled}"
            name="${this.name}"
            placeholder="${this.placeholder}"
            ?readonly="${this.readOnly}"
            ?required="${this.required}"
            @change="${__classPrivateFieldGet(this, _FsCurrencyField_instances, "m", _FsCurrencyField_setInstanceAndFormValue)}"
            @input="${__classPrivateFieldGet(this, _FsCurrencyField_instances, "m", _FsCurrencyField_setInstanceAndFormValue)}"
            @keydown="${__classPrivateFieldGet(this, _FsCurrencyField_instances, "m", _FsCurrencyField_onKeydown)}"
            pattern="[0-9]+"
            part="fs-input__input" />
        </div>
      ${this.noValidate
            ? nothing
            : html `
            <span
              class="fs-helper-text 
              ${this._showError && !this.disabled ? 'fs-helper-text--error' : ''}
              ${this.disabled ? 'fs-helper-text--disabled' : ''}"
              id="helper-text"
              aria-atomic="true"
              role="alert">
                ${this._showError && !this.disabled ? this.validationMessage : this.helperText}
            </span>`}
    </div>`;
    }
    /** Lit lifecycle Respond to changes */
    updated(changed) {
        /**
         * When the value property or attribute is set not in alignment with
         * the firstUpdated lifecycle method, respond by setting the manager's value
         * alongside the FormControl's value to trigger validations.
         */
        if (changed.has('value') && this.value !== this.manager.unmaskedValue) {
            this.manager.setValue(this.value);
            this.setValue(this.manager.unmaskedValue);
        }
        /** Update the manager configuration if any config key changes */
        if (changed.has('currencySign') ||
            changed.has('currencySignPosition') ||
            changed.has('decimalSeparator') ||
            changed.has('max') ||
            changed.has('maximumFractionDigits') ||
            changed.has('min') ||
            changed.has('minimumDecimalPrecision') ||
            changed.has('minimumFractionDigits') ||
            changed.has('thousandsSeparator') ||
            changed.has('useMaskedValue')) {
            this.manager.updateConfig(this.managerConfig);
        }
        if (changed.has('errorMessage')) {
            this.internals.reportValidity();
        }
        if (changed.has('useMaskedValue')) {
            this.setValue(this.preferredValue);
        }
    }
    /**
     * Override the default validator messages by keying off the provided
     * validationKey argument
     */
    validityCallback(validationKey) {
        switch (validationKey) {
            case 'valueMissing':
                return this.valueMissingMessage ? this.valueMissingMessage : this.valueMissingMessage;
            case 'rangeOverflow':
                return this.rangeOverflowMessage ? this.rangeOverflowMessage : null;
            case 'rangeUnderflow':
                return this.rangeUnderflowMessage ? this.rangeUnderflowMessage : null;
            case 'customError':
                return this.errorMessage ? this.errorMessage : null;
            default:
                return;
        }
    }
    /**
     * Lifecycle method from the FormControlMixin
     *
     * Here we capture any updates to the validation message coming from
     * the validators and set the error and override the helper text
     */
    validationMessageCallback(message) {
        this.validationMessage = message;
        this._showError = !!message;
    }
}
_FsCurrencyField_onInvalid = new WeakMap(), _FsCurrencyField_instances = new WeakSet(), _FsCurrencyField_onKeydown = function _FsCurrencyField_onKeydown(event) {
    if (event.code === 'Enter' && this.form) {
        submit(this.form);
    }
}, _FsCurrencyField_setInstanceAndFormValue = function _FsCurrencyField_setInstanceAndFormValue(event) {
    this.value = this.manager.unmaskedValue;
    this.setValue(this.preferredValue);
    if (event.type === 'change') {
        this.dispatchEvent(new Event(event.type, event));
    }
};
FsCurrencyField.styles = [
    labelStyles,
    helperTextStyles,
    formFieldStyles,
    textFieldStyles,
    css `:host {
      display: block;
    }`
];
/** Set the canonical name for use with the safe define utility */
FsCurrencyField.canonicalName = canonicalName;
/** Currency field validators */
FsCurrencyField.formControlValidators = [
    setCustomValidity,
    maxValidatorCurrency,
    minValidatorCurrency,
    requiredValidator
];
/** Delegate the focus to the internal input (this.validationTarget) */
FsCurrencyField.shadowRootOptions = {
    delegatesFocus: true,
    mode: 'open'
};
__decorate([
    property()
], FsCurrencyField.prototype, "autocomplete", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsCurrencyField.prototype, "autofocus", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsCurrencyField.prototype, "compact", void 0);
__decorate([
    property({ type: String, attribute: 'currency-sign' })
], FsCurrencyField.prototype, "currencySign", void 0);
__decorate([
    property({ type: String, attribute: 'currency-sign-position' })
], FsCurrencyField.prototype, "currencySignPosition", void 0);
__decorate([
    property({ type: String, attribute: 'decimal-separator' })
], FsCurrencyField.prototype, "decimalSeparator", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsCurrencyField.prototype, "disabled", void 0);
__decorate([
    property({ type: String, attribute: 'helper-text' })
], FsCurrencyField.prototype, "helperText", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-label' })
], FsCurrencyField.prototype, "hideLabel", void 0);
__decorate([
    property({ type: Number, reflect: true })
], FsCurrencyField.prototype, "max", void 0);
__decorate([
    property({ type: Number, attribute: 'minimum-decimal-percision' })
], FsCurrencyField.prototype, "minimumDecimalPrecision", void 0);
__decorate([
    property({ type: Number, attribute: 'maximum-fraction-digits' })
], FsCurrencyField.prototype, "maximumFractionDigits", void 0);
__decorate([
    property({ type: Number, reflect: true })
], FsCurrencyField.prototype, "min", void 0);
__decorate([
    property({ type: String, attribute: 'error-message', reflect: true })
], FsCurrencyField.prototype, "errorMessage", void 0);
__decorate([
    property({ type: Number, attribute: 'minimum-fraction-digits' })
], FsCurrencyField.prototype, "minimumFractionDigits", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsCurrencyField.prototype, "name", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-validate' })
], FsCurrencyField.prototype, "noValidate", void 0);
__decorate([
    property()
], FsCurrencyField.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, attribute: 'range-overflow-message' })
], FsCurrencyField.prototype, "rangeOverflowMessage", void 0);
__decorate([
    property({ type: String, attribute: 'range-underflow-message' })
], FsCurrencyField.prototype, "rangeUnderflowMessage", void 0);
__decorate([
    property({ type: Boolean, attribute: 'readonly' })
], FsCurrencyField.prototype, "readOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsCurrencyField.prototype, "required", void 0);
__decorate([
    property({ type: String, attribute: 'thousands-separator' })
], FsCurrencyField.prototype, "thousandsSeparator", void 0);
__decorate([
    property({ type: Boolean, attribute: 'use-masked-value' })
], FsCurrencyField.prototype, "useMaskedValue", void 0);
__decorate([
    property()
], FsCurrencyField.prototype, "value", void 0);
__decorate([
    state()
], FsCurrencyField.prototype, "validationMessage", void 0);
__decorate([
    property({ type: String, attribute: 'value-missing-message', reflect: false })
], FsCurrencyField.prototype, "valueMissingMessage", void 0);
__decorate([
    state()
], FsCurrencyField.prototype, "_showError", void 0);
__decorate([
    query('input')
], FsCurrencyField.prototype, "validationTarget", void 0);
//# sourceMappingURL=CurrencyField.js.map